import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { timetableService, staffService, type TimetableEntry, type TimetableImportSummary, type Weekday } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Calendar, Upload } from "lucide-react";
import { toast } from "sonner";

const DAYS: Weekday[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function TimetablePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const [klass, setKlass] = useState<string>(user?.class_name || "Grade 5A");
  const [creating, setCreating] = useState(false);
  const [importSummary, setImportSummary] = useState<TimetableImportSummary | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const qcRoot = useQueryClient();

  // For students/parents, force their own class
  const lockedClass = user && (user.role === "student" || user.role === "parent");
  const effectiveClass = lockedClass ? user.class_name || klass : klass;

  const importMut = useMutation({
    mutationFn: (f: File) => timetableService.importXlsx(f),
    onSuccess: (res) => {
      setImportSummary(res);
      toast.success(`Imported ${res.entries_created} slots across ${res.classes_imported} classes`);
      qcRoot.invalidateQueries({ queryKey: ["timetable"] });
      qcRoot.invalidateQueries({ queryKey: ["timetable-classes"] });
      qcRoot.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e: any) => toast.error(e.message || "Import failed"),
  });

  const { data: entries = [], isPending } = useQuery({
    queryKey: ["timetable", effectiveClass],
    queryFn: () => timetableService.list(effectiveClass),
    enabled: !!effectiveClass,
  });

  // For admin, get all classes from /students
  const { data: classes = [] } = useQuery({
    queryKey: ["timetable-classes"],
    queryFn: async () => {
      // derive list of classes from existing timetable entries (no extra api call)
      const all = await timetableService.list();
      const set = new Set<string>(all.map((e) => e.class_name));
      // also add user.class_name if present
      if (user?.class_name) set.add(user.class_name);
      return Array.from(set).sort();
    },
    enabled: isAdmin || user?.role === "teacher",
  });

  // Build a [day][period] grid from entries
  const grid = useMemo(() => {
    const g: Record<Weekday, Record<number, TimetableEntry>> = {
      Mon: {}, Tue: {}, Wed: {}, Thu: {}, Fri: {}, Sat: {},
    };
    let maxP = 1;
    for (const e of entries) {
      g[e.day] = g[e.day] || {};
      g[e.day][e.period] = e;
      if (e.period > maxP) maxP = e.period;
    }
    return { grid: g, periods: Array.from({ length: Math.max(maxP, 6) }, (_, i) => i + 1) };
  }, [entries]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Timetable</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Weekly schedule {effectiveClass ? `for ${effectiveClass}` : ""}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <input
              ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) importMut.mutate(f);
                if (fileRef.current) fileRef.current.value = "";
              }}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importMut.isPending}>
              {importMut.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Upload className="mr-2 h-4 w-4" />}
              Import Excel
            </Button>
            <Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add slot</Button>
          </div>
        )}
      </div>

      {isAdmin && importSummary && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
            <div className="text-sm">
              <div className="font-semibold text-emerald-700">
                ✓ Imported {importSummary.filename ?? "timetable"}
              </div>
              <div className="text-muted-foreground">
                {importSummary.classes_imported} classes · {importSummary.entries_created} slots ·
                {" "}{importSummary.staff_created > 0
                  ? `${importSummary.staff_created} new teachers provisioned`
                  : "no new teachers"}
                {importSummary.warnings.length > 0 && ` · ${importSummary.warnings.length} warning(s)`}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setImportSummary(null)}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      {!lockedClass && (
        <Card><CardContent className="flex flex-wrap items-end gap-2 p-4">
          <div>
            <Label>Class</Label>
            <div className="mt-1 flex flex-wrap gap-1">
              {classes.length === 0 && <Badge variant="outline">No classes yet</Badge>}
              {classes.map((c) => (
                <Button key={c} size="sm" variant={klass === c ? "default" : "outline"} onClick={() => setKlass(c)}>{c}</Button>
              ))}
            </div>
          </div>
        </CardContent></Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : entries.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <Calendar className="h-8 w-8" />
              <div className="text-sm">No timetable for <span className="font-semibold">{effectiveClass}</span> yet.</div>
              {isAdmin && <Button size="sm" onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add the first slot</Button>}
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="w-24 p-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Period</th>
                  {DAYS.map((d) => (
                    <th key={d} className="p-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.periods.map((p) => (
                  <tr key={p} className="border-b">
                    <td className="bg-slate-50 p-2 text-center font-bold text-muted-foreground">{p}</td>
                    {DAYS.map((d) => {
                      const e = grid.grid[d]?.[p];
                      return (
                        <td key={d} className="p-2 align-top">
                          {e ? (
                            <SlotCell entry={e} canEdit={isAdmin} />
                          ) : (
                            <div className="h-16 rounded-md border border-dashed border-slate-200" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {creating && <SlotDialog defaultClass={effectiveClass} onClose={() => setCreating(false)} />}
    </div>
  );
}

function SlotCell({ entry, canEdit }: { entry: TimetableEntry; canEdit: boolean }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => timetableService.remove(entry.id),
    onSuccess: () => { toast.info("Slot removed"); qc.invalidateQueries({ queryKey: ["timetable"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <div className="group relative h-16 rounded-md border bg-primary/5 p-2">
      <div className="text-sm font-semibold leading-tight">{entry.subject}</div>
      <div className="text-[10px] text-muted-foreground">{entry.teacher_name}</div>
      <div className="text-[10px] text-muted-foreground">{entry.start_time} – {entry.end_time}</div>
      {canEdit && (
        <button
          onClick={() => { if (confirm(`Remove ${entry.subject} on ${entry.day} P${entry.period}?`)) del.mutate(); }}
          className="absolute right-1 top-1 hidden h-5 w-5 items-center justify-center rounded-full bg-white shadow group-hover:flex hover:bg-red-50"
        >
          <Trash2 className="h-3 w-3 text-destructive" />
        </button>
      )}
    </div>
  );
}

function SlotDialog({ defaultClass, onClose }: { defaultClass: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: () => staffService.list() });
  const [f, setF] = useState({
    class_name: defaultClass, day: "Mon" as Weekday, period: 1,
    subject: "", teacher_name: staff[0]?.name || "",
    start_time: "08:00", end_time: "08:45",
  });

  const save = useMutation({
    mutationFn: () => timetableService.create(f),
    onSuccess: () => {
      toast.success("Slot added");
      qc.invalidateQueries({ queryKey: ["timetable"] });
      qc.invalidateQueries({ queryKey: ["timetable-classes"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add timetable slot</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Class</Label><Input value={f.class_name} onChange={(e) => setF({ ...f, class_name: e.target.value })} /></div>
          <div><Label>Day</Label>
            <div className="mt-1 flex flex-wrap gap-1">
              {DAYS.map((d) => (
                <Button key={d} size="sm" variant={f.day === d ? "default" : "outline"} type="button" onClick={() => setF({ ...f, day: d })}>{d}</Button>
              ))}
            </div>
          </div>
          <div><Label>Period</Label><Input type="number" min={1} max={12} value={f.period} onChange={(e) => setF({ ...f, period: parseInt(e.target.value || "1") })} /></div>
          <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Mathematics" /></div>
          <div className="col-span-2"><Label>Teacher</Label>
            {staff.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {staff.map((s) => (
                  <Button key={s.id} size="sm" type="button"
                    variant={f.teacher_name === s.name ? "default" : "outline"}
                    onClick={() => setF({ ...f, teacher_name: s.name })}>
                    {s.name}
                  </Button>
                ))}
              </div>
            ) : (
              <Input value={f.teacher_name} onChange={(e) => setF({ ...f, teacher_name: e.target.value })} placeholder="Teacher name" />
            )}
          </div>
          <div><Label>Start</Label><Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
          <div><Label>End</Label><Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !f.subject || !f.teacher_name}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add slot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
